import React, { useEffect, useState } from "react";
import { Row, Col, Card } from "react-bootstrap";
import { useAuth0 } from "@auth0/auth0-react";
import Dialog from "components/organisms/Dialog/Dialog";
import Button from "components/atoms/Button/Button";
import { toast } from "react-toastify";
import { FormattedMessage, useIntl } from "react-intl";
import { getProviderPerformanceReview, updateProviderPerformanceReview } from "api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import errorToast from "helpers/errorToast";
import PropTypes from "prop-types";
import LoadingSpinner from "components/atoms/LoadingSpinner/LoadingSpinner";
import Star from "../Star/Star";
import "./RateNowDialog.scss";

function RateNowDialog({ isOpen, close, id }) {
  const intl = useIntl();
  const { user } = useAuth0();
  const [performanceReview, setPerformanceReview] = useState([]);
  const [ontime_performance_rating, setOntime_performance_rating] = useState("");
  const [communication_rating, setCommunication_rating] = useState("");
  const [pricing_rating, setPricing_rating] = useState("");
  const [overall_performance, setOverall_performance] = useState("");
  const [loading, setLoading] = useState(true);

  const handleChange = (name, value) => {
    if (name === "ontime_performance_rating") {
      setOntime_performance_rating(value);
    } else if (name === "communication_rating") {
      setCommunication_rating(value);
    } else if (name === "pricing_rating") {
      setPricing_rating(value);
    } else {
      setOverall_performance(value);
    }
  };

  const handleRate = (value) => {
    if (value === 1) return "Poor";
    if (value === 2) return "Fair";
    if (value === 3) return "Good";
    if (value === 4) return "Very good";
    if (value === 5) return "Excellent";
  };

  const handlePostReview = () => {
    const payload = {
      submitted_by: user.email,
      ontime_performance_rating,
      communication_rating,
      pricing_rating,
      overall_performance,
    };
    updateProviderPerformanceReview(id, payload)
      .then((res) => {
        if (res.status === 200) {
          toast("Performance review submitted successfully", { type: "success" });
          close();
        }
      })
      .catch((err) => errorToast(err));
  };

  const handleValidation = () => {
    if (
      ontime_performance_rating === "" ||
      communication_rating === "" ||
      pricing_rating === "" ||
      overall_performance === ""
    ) {
      return true;
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    if (id) {
      setLoading(true);
      getProviderPerformanceReview(id).then((res) => {
        setPerformanceReview(res.data);
        setLoading(false);
      });
    }
  }, [id, isOpen]);

  useEffect(() => {
    setOntime_performance_rating(performanceReview?.ontime_performance_rating);
    setCommunication_rating(performanceReview?.communication_rating);
    setPricing_rating(performanceReview?.pricing_rating);
    setOverall_performance(performanceReview?.overall_performance_rating);
  }, [performanceReview]);

  return (
    <Dialog
      title={intl.formatMessage({
        id: "tracking.rateProvider",
        defaultMessage: "Rate provider",
      })}
      size="md"
      show={isOpen}
      onHide={close}
    >
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          <Card.Text>
            <FormattedMessage id="review.provider" defaultMessage="Provider">
              {(...label) => <span className="text-secondary">{label}</span>}
            </FormattedMessage>
            <span className="font-weight-bold d-flex float-left align-dialog">{performanceReview.delivered_by}</span>
          </Card.Text>
          <Card.Text>
            <FormattedMessage id="review.shipment" defaultMessage="Shipment">
              {(...label) => <span className="text-secondary">{label}</span>}
            </FormattedMessage>
            <span className="font-weight-bold d-flex float-left align-dialog">{performanceReview.reference_number}</span>
          </Card.Text>
          <Card.Text>
            <FormattedMessage id="review.freightMode" defaultMessage="Freight mode">
              {(...label) => <span className="text-secondary">{label}</span>}
            </FormattedMessage>
            <span className="font-weight-bold d-flex float-left align-dialog">{performanceReview.freight_mode}</span>
          </Card.Text>
          <Card.Text>
            <FormattedMessage id="review.fromTo" defaultMessage="From-To">
              {(...label) => <span className="text-secondary max_media_mobile">{label}</span>}
            </FormattedMessage>
            <span className="font-weight-bold float-left width-50 max_media_mobile">
              {performanceReview.drop_off_city_name}, {performanceReview.drop_off_country_name} -
              {performanceReview.pickup_city_name}, {performanceReview.pickup_country_name}
            </span>
          </Card.Text>
          <Card.Text>
            <FormattedMessage id="review.podDate" defaultMessage="POD Date">
              {(...label) => <span className="text-secondary">{label}</span>}
            </FormattedMessage>
            <span className="font-weight-bold d-flex float-left align-dialog">{performanceReview.pod_date}</span>
          </Card.Text>
          <Card.Text>
            <FormattedMessage id="review.quotedEta" defaultMessage="Quoted ETA">
              {(...label) => <span className="text-secondary">{label}</span>}
            </FormattedMessage>
            <span className="font-weight-bold d-flex float-left align-dialog">{performanceReview.quoted_eta}</span>
          </Card.Text>
          <div className="mt-4">
            <Card.Text>
              <FormattedMessage id="review.onTimePerformance" defaultMessage="On-time Performance">
                {(...label) => (
                  <span className="text-secondary">
                    {label} <span className="text-danger">*</span>
                  </span>
                )}
              </FormattedMessage>
              <span className="font-weight-bold d-flex float-left align-dialog">
                <Star
                  name="ontime_performance_rating"
                  color="#FDB833"
                  handleChange={handleChange}
                  count={ontime_performance_rating}
                  hover
                />
                <span className="pl-4 min-width-100 max_media_mobile">
                  {`${
                    handleRate(ontime_performance_rating)
                      ? handleRate(ontime_performance_rating)
                      : performanceReview.ontime_performance_rating
                  }`}{" "}
                </span>
                {ontime_performance_rating && (
                  <span className="pl-4 max_media_mobile">
                    <FontAwesomeIcon icon={faCheck} color="green" />
                  </span>
                )}
              </span>
            </Card.Text>
            <Card.Text>
              <FormattedMessage id="review.communication" defaultMessage="Communication">
                {(...label) => (
                  <span className="text-secondary mt-2">
                    {label}
                    <span className="text-danger">*</span>
                  </span>
                )}
              </FormattedMessage>
              <span className="font-weight-bold mt-2 d-flex align-dialog">
                <Star
                  name="communication_rating"
                  color="#FDB833"
                  handleChange={handleChange}
                  count={communication_rating}
                  hover
                />
                <span className="pl-4 min-width-100 max_media_mobile">
                  {`${
                    handleRate(communication_rating)
                      ? handleRate(communication_rating)
                      : performanceReview.communication_rating
                  }`}
                </span>
                {communication_rating && (
                  <span className="pl-4 max_media_mobile">
                    <FontAwesomeIcon icon={faCheck} color="green" />
                  </span>
                )}
              </span>
            </Card.Text>
            <Card.Text>
              <FormattedMessage id="review.pricing" defaultMessage="Pricing">
                {(...label) => (
                  <span className="text-secondary mt-2">
                    {label} <span className="text-danger">*</span>
                  </span>
                )}
              </FormattedMessage>
              <span className="font-weight-bold mt-2 d-flex align-dialog">
                <Star name="pricing_rating" color="#FDB833" handleChange={handleChange} count={pricing_rating} hover />
                <span className="pl-4 min-width-100 max_media_mobile">
                  {`${handleRate(pricing_rating) ? handleRate(pricing_rating) : performanceReview.pricing_rating}`}
                </span>
                {pricing_rating && (
                  <span className="pl-4 max_media_mobile">
                    <FontAwesomeIcon icon={faCheck} color="green" />
                  </span>
                )}
              </span>
            </Card.Text>
            <Card.Text>
              <FormattedMessage id="review.overallPerformance" defaultMessage="Overall performance">
                {(...label) => (
                  <span className="text-secondary mt-2">
                    {label} <span className="text-danger">*</span>
                  </span>
                )}
              </FormattedMessage>
              <span className="font-weight-bold mt-2 d-flex align-dialog">
                <Star
                  name="overall_performance"
                  color="#FDB833"
                  handleChange={handleChange}
                  count={overall_performance}
                  hover
                />
                <span className="pl-4 min-width-100 max_media_mobile">
                  {`${
                    handleRate(overall_performance)
                      ? handleRate(overall_performance)
                      : performanceReview.overall_performance_rating
                  }`}
                </span>
                {overall_performance && (
                  <span className="pl-4 max_media_mobile">
                    <FontAwesomeIcon icon={faCheck} color="green" />
                  </span>
                )}
              </span>
            </Card.Text>
          </div>
          <Row className="mt-5">
            <Col lg={12} className="d-flex flex-row">
              <Button
                className="btn-grey"
                label={intl.formatMessage({
                  id: "general.cancel",
                  defaultMessage: "Cancel",
                })}
                onClick={close}
              />
              <Button
                className="ml-auto"
                label={intl.formatMessage({
                  id: "general.submit",
                  defaultMessage: "Submit",
                })}
                variant="success"
                type="submit"
                onClick={handlePostReview}
                disabled={handleValidation()}
              />
            </Col>
          </Row>
        </>
      )}
    </Dialog>
  );
}

RateNowDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
};

export default RateNowDialog;
